/**
 * GPill — tag/chip primitive (STORY-000-006a).
 *
 * Used across filter rows, status pills, citation chips, and any
 * other small chip affordance. When `onClick` is supplied, renders
 * as a focusable `<button>`; otherwise renders as a passive `<span>`
 * (no implicit role, no tab stop) — story AC #9.
 *
 * Tone mapping (theme.css `--color-*` chain via shadcn utilities):
 *   neutral -> bg-card + border + text-foreground
 *   primary -> bg-secondary + text-secondary-foreground (soft primary)
 *   accent  -> bg-[var(--g-accent-soft)] + text-accent
 *   success -> tinted --g-success bg + text-[var(--g-success)]
 *   warn    -> tinted --g-warn bg    + text-[var(--g-warn)]
 *   error   -> tinted --g-error bg   + text-[var(--g-error)]
 *
 * `active` toggles the filled state for `neutral` and `primary`
 * tones — used by filter chips that have a selected state. The semantic
 * tones (success/warn/error) and `accent` are one-shot looks and ignore
 * `active` (matching the prototype).
 *
 * Visual reference: docs/GardEn/src/ui.jsx `GPill` +
 * docs/GardEn/handoff-2026-06/garden/ui.jsx `Pill` (success/warn/error tones).
 */
import type { ReactElement, ReactNode } from "react";

import { Icon, type IconName, isIconName } from "./Icon";

export type GPillTone =
  | "neutral"
  | "primary"
  | "accent"
  | "success"
  | "warn"
  | "error";
export type GPillSize = "sm" | "md";

export interface GPillProps {
  /** Visual tone. Defaults to `neutral`. */
  tone?: GPillTone;
  /** Pill size. Defaults to `md`. */
  size?: GPillSize;
  /** Active/selected state (filter-chip selected look). */
  active?: boolean;
  /** Optional leading slot. Either a design {@link IconName} (rendered as
   * an `<Icon>`) or any `ReactNode` (rendered verbatim) — backward compatible
   * with the original `ReactNode`-only slot. */
  leading?: IconName | ReactNode;
  /** Click handler. Presence promotes the element to a `<button>`. */
  onClick?: () => void;
  /**
   * Let the label wrap to a second line instead of running on one fixed-height
   * line. Use for pills that carry a sentence (chat follow-ups), never for
   * filter chips, where uniform height is the point.
   */
  wrap?: boolean;
  /** Additional classes. */
  className?: string;
  /** Pill content (label, often i18n-translated at the call site). */
  children: ReactNode;
}

const sizeClasses: Record<GPillSize, string> = {
  sm: "h-6 px-2.5 text-xs",
  md: "h-8 px-3 text-sm",
};

/**
 * Wrapping variant of {@link sizeClasses}.
 *
 * A pill is a fixed-height, non-wrapping chip by default, which is right for a
 * filter or a status mark. It is wrong for a chat follow-up suggestion, where
 * the pill carries a whole question ("Сколько клея нужно на 12 м² при плитке
 * 30×30?") and a nowrap chip either overflows the rail or clamps the sentence
 * to a fragment the user cannot choose from. `wrap` swaps the fixed height for
 * a minimum and lets the text run to a second line.
 */
const wrapSizeClasses: Record<GPillSize, string> = {
  sm: "min-h-6 px-2.5 py-1 text-xs",
  md: "min-h-8 px-3 py-1.5 text-sm",
};

/**
 * Token-mapped tone classes. Active state for `neutral` and
 * `primary` toggles to the filled background; `accent` and the semantic
 * tones (success/warn/error) do not have a separate active state in the
 * prototype (they're one-shot looks), so they ignore `active`. The semantic
 * tints use the `/10` arbitrary-opacity idiom (matching GConfidence) over the
 * solid `--g-success`/`--g-warn`/`--g-error` tokens.
 */
function toneClasses(tone: GPillTone, active: boolean): string {
  switch (tone) {
    case "neutral":
      // Active was `bg-foreground` — the INK token as a fill, which renders a
      // near-black chip (#1B1714) that belongs to no GardEn palette and read,
      // on the Shops filter row, as a stray control from another product. An
      // active filter is a brand state, so it takes the brand fill; the token
      // is still a token, it was simply the wrong one.
      return active
        ? "bg-primary text-[var(--g-on-primary)] border border-transparent"
        : "bg-card text-foreground border border-border";
    case "primary":
      return active
        ? "bg-primary text-primary-foreground border border-transparent"
        : "bg-secondary text-secondary-foreground border border-transparent";
    case "accent":
      return "bg-[var(--g-accent-soft)] text-accent border border-transparent";
    case "success":
      return "bg-[var(--g-success)]/10 text-[var(--g-success)] border border-transparent";
    case "warn":
      return "bg-[var(--g-warn)]/10 text-[var(--g-warn)] border border-transparent";
    case "error":
      return "bg-[var(--g-error)]/10 text-[var(--g-error)] border border-transparent";
  }
}

/**
 * Resolves the `leading` slot: a known {@link IconName} string renders as an
 * `<Icon>` (sized to the pill), anything else (incl. arbitrary `ReactNode`)
 * renders verbatim — preserving the original `ReactNode`-only contract.
 */
function renderLeading(
  leading: IconName | ReactNode,
  size: GPillSize,
): ReactNode {
  if (isIconName(leading)) {
    return <Icon name={leading} size={size === "sm" ? 13 : 15} />;
  }
  return leading;
}

/**
 * Renders a pill chip. Polymorphic by `onClick`:
 *  - With `onClick`: focusable `<button type="button">`.
 *  - Without `onClick`: passive `<span>` (no focus, no implicit role).
 */
export function GPill({
  tone = "neutral",
  size = "md",
  active = false,
  leading,
  onClick,
  wrap = false,
  className,
  children,
}: GPillProps): ReactElement {
  const baseClasses = [
    "inline-flex",
    "items-center",
    "gap-1.5",
    "rounded-full",
    "font-medium",
    wrap ? "text-left" : "whitespace-nowrap",
    "transition-colors",
    wrap ? wrapSizeClasses[size] : sizeClasses[size],
    toneClasses(tone, active),
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const interactiveClasses = onClick
    ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    : "";
  const finalClasses = [baseClasses, interactiveClasses]
    .filter(Boolean)
    .join(" ");

  if (onClick) {
    return (
      <button
        type="button"
        data-testid="garden-pill"
        data-tone={tone}
        data-size={size}
        data-active={active ? "true" : "false"}
        onClick={onClick}
        className={finalClasses}
      >
        {renderLeading(leading, size)}
        <span>{children}</span>
      </button>
    );
  }

  return (
    <span
      data-testid="garden-pill"
      data-tone={tone}
      data-size={size}
      data-active={active ? "true" : "false"}
      className={finalClasses}
    >
      {renderLeading(leading, size)}
      <span>{children}</span>
    </span>
  );
}

/**
 * `Pill` — design-revamp alias for {@link GPill}. The handoff-2026-06 spec
 * names this primitive `Pill`; existing consumers keep importing `GPill`.
 */
export { GPill as Pill };
